"use client"

import { RolePreview } from "@/components/JobApplication/RolePreview"
import { Image, Text } from "@/elements"
import { FragmentType, useFragment } from "@/gql"
import { getFragmentData } from "@/gql-clients/experimental/getFragmentData"
import { JobApplicationProjectPreviewFragmentDoc } from "@/gql/graphql"
import { BriefcaseIcon, CameraIcon } from "@/icons/solid"
import clsx from "clsx"
import { useMemo } from "react"

interface Props {
  projectData: FragmentType<typeof JobApplicationProjectPreviewFragmentDoc> | undefined | null
  creativeWorkAttachments: FragmentType<typeof JobApplicationProjectPreviewFragmentDoc>[]
  toggleProject: (project: FragmentType<typeof JobApplicationProjectPreviewFragmentDoc>) => void
  positionId: string | undefined
}

export const ProjectSelectionCard: React.FC<Props> = ({
  creativeWorkAttachments,
  toggleProject,
  positionId,
  ...props
}) => {
  const projectData = useFragment(JobApplicationProjectPreviewFragmentDoc, props.projectData)

  const selectionIndex = useMemo(() => {
    return getFragmentData(JobApplicationProjectPreviewFragmentDoc, creativeWorkAttachments)
      .map(creativeWorkEdge => creativeWorkEdge.id)
      .indexOf(projectData?.id || "")
  }, [creativeWorkAttachments, projectData?.id])

  const isSelected = selectionIndex >= 0
  if (!projectData || !props.projectData) return null
  const xPosition = projectData?.thumbnailFocalPointX ?? 0.5
  const yPosition = projectData?.thumbnailFocalPointY ?? 0.5
  return (
    <div className="relative h-fit cursor-default">
      <Image
        srcSetUrls={{
          sm: projectData.thumbnail645w || "",
        }}
        alt={projectData.title || ""}
        className="aspect-square w-full cursor-pointer select-none rounded-md object-cover"
        onClick={() => {
          if (props.projectData) toggleProject(props.projectData)
        }}
        style={{
          objectPosition: `${xPosition * 100}% ${yPosition * 100}%`,
        }}
      />
      {/* selected project indicator */}
      <div
        className={clsx(
          "absolute right-4 top-4 flex size-7 cursor-pointer select-none items-center justify-center rounded-full text-fg-1",
          isSelected
            ? "bg-bg-1"
            : "border border-bg-1 border-opacity-70 bg-bg-1 bg-opacity-10 shadow-[0_4px_24px_0_rgba(0,0,0,0.10)] backdrop-blur-[10px]"
        )}
        onClick={() => {
          if (props.projectData) toggleProject(props.projectData)
        }}>
        {isSelected ? <Text variant="CTASmall">{selectionIndex + 1}</Text> : null}
      </div>
      <div className="flex flex-col pb-3 pt-2">
        <Text className="pb-1" maxLines={1} variant="body2">
          {projectData.title}
        </Text>
        <RolePreview creativeWork={projectData} positionId={positionId} />
        {projectData.category ? (
          <div className="flex flex-row items-center gap-x-[6px]">
            <BriefcaseIcon width={12} height={12} className="shrink-0 fill-fg-2 stroke-fg-2" />
            <Text variant="caption" className="text-fg-2">
              {projectData.category?.name}
            </Text>
          </div>
        ) : null}
      </div>
    </div>
  )
}
